// DesktopSelector.js (FULL DESKTOP VERSION — premium, glassy, non-chunky)
// Drop-in replacement for your DesktopSelector component file.

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

/**
 * DesktopSelector (premium + fast jump)
 *
 * ✅ Behavior:
 * - flyt-og-rytme: labels from filename → 12A / 12B
 * - if tabLabelMap matches: label from map (basename -> uiTitle)
 * - else: numeric parts
 */

function filenameOf(tab) {
  if (!tab) return "";
  if (typeof tab === "string") return tab;
  if (typeof tab === "object") {
    if (typeof tab.file === "string") return tab.file;
    if (typeof tab.full_path === "string") return tab.full_path;
    if (typeof tab.path === "string") return tab.path;
  }
  return "";
}

function basenameRaw(filename) {
  if (!filename) return "";
  return String(filename).split("/").pop() || "";
}

function basenameOfTab(tab) {
  return basenameRaw(filenameOf(tab));
}

function normKey(s) {
  return String(s || "")
    .replace(/\r/g, "")
    .replace(/\u00A0/g, " ")
    .trim()
    .toLowerCase();
}

function mapGet(tabLabelMap, rawKey) {
  if (!tabLabelMap || !rawKey) return null;
  const kNorm = normKey(rawKey);

  if (typeof tabLabelMap.get === "function") {
    return tabLabelMap.get(kNorm) || tabLabelMap.get(rawKey) || null;
  }
  return tabLabelMap[kNorm] || tabLabelMap[rawKey] || null;
}

function labelFromMap(tabLabelMap, tabsData, idx) {
  if (!tabLabelMap) return null;
  const rawBase = basenameOfTab(tabsData?.[idx]);
  if (!rawBase) return null;
  return mapGet(tabLabelMap, rawBase);
}

// "01a_xxx.mp3" -> "1a"
function flytOgRytmeLabelFromFilename(tab) {
  const base = basenameOfTab(tab);
  const m = base.match(/^(\d{1,2})([ab])/i);
  if (!m) return null;
  const num = parseInt(m[1], 10);
  const letter = m[2].toLowerCase();
  if (!Number.isFinite(num)) return null;
  return `${num}${letter}`;
}

function defaultLabel(idx) {
  return String(idx + 1);
}

function getTabLabel({ tabsData, idx, courseKey, tabLabelMap }) {
  if (courseKey === "flyt-og-rytme") {
    const l = flytOgRytmeLabelFromFilename(tabsData?.[idx]);
    if (l) return l.toUpperCase();
    return defaultLabel(idx);
  }

  const mapped = labelFromMap(tabLabelMap, tabsData, idx);
  if (mapped) return mapped;

  return defaultLabel(idx);
}

function parseFlytLabel(label) {
  const m = String(label || "").match(/^(\d{1,2})([AB])$/i);
  if (!m) return null;
  return { n: parseInt(m[1], 10), letter: m[2].toUpperCase() };
}

function buildFlytGroups(tabsData, courseKey) {
  if (courseKey !== "flyt-og-rytme") return null;
  const groups = new Map();
  for (let i = 0; i < (tabsData?.length ?? 0); i++) {
    const l = flytOgRytmeLabelFromFilename(tabsData?.[i]);
    if (!l) continue;
    const p = parseFlytLabel(l.toUpperCase());
    if (!p) continue;
    if (!groups.has(p.n)) groups.set(p.n, {});
    groups.get(p.n)[p.letter] = i;
  }
  return Array.from(groups.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([n, v]) => ({ n, ...v }));
}

// ---------- icons
const IconFirst = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
  </svg>
);
const IconPrev = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);
const IconNext = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);
const IconLast = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
  </svg>
);
const IconChevronDown = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);
const IconCheck = () => (
  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

// ---------- small ui pieces (premium-on-blue)
const NavBtn = ({ title, onClick, disabled, children }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    title={title}
    className={[
      "flex items-center justify-center w-8 h-8 rounded-lg border transition-all duration-200 flex-shrink-0",
      disabled
        ? "bg-white/10 text-white/35 border-white/10 cursor-not-allowed"
        : "bg-white/10 hover:bg-white/15 text-white/85 border-white/15 hover:border-white/25 hover:shadow-sm active:scale-[0.98]",
    ].join(" ")}
  >
    {children}
  </button>
);

const PillBtn = ({ title, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white/90 border border-white/15 hover:border-white/25 hover:shadow-sm transition-all duration-200 text-sm font-semibold active:scale-[0.99]"
  >
    {children}
  </button>
);

const GridBtn = ({ active, onClick, children, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={[
      "px-2 py-1 rounded-lg border text-xs font-semibold transition-all duration-150",
      disabled
        ? "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed"
        : active
        ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-blue-400/30 shadow-sm"
        : "bg-white text-gray-700 border-gray-200 hover:bg-blue-50",
    ].join(" ")}
  >
    {children}
  </button>
);

const DesktopSelector = ({ tabsData, clickHandler, active = 0, courseKey, tabLabelMap = null }) => {
  const total = Array.isArray(tabsData) ? tabsData.length : 0;
  const activeIdx = total > 0 ? Math.max(0, Math.min(total - 1, active)) : 0;

  const [isOpen, setIsOpen] = useState(false);
  const [q, setQ] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const labelFor = useCallback(
    (idx) => getTabLabel({ tabsData, idx, courseKey, tabLabelMap }),
    [tabsData, courseKey, tabLabelMap]
  );

  const handleTabClick = useCallback(
    (idx) => {
      if (!total) return;
      const nextIdx = Math.max(0, Math.min(total - 1, idx));
      clickHandler?.(nextIdx);
    },
    [clickHandler, total]
  );

  // outside click closes
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ESC closes, "/" or "g" opens
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false);

      const tag = (e.target?.tagName || "").toLowerCase();
      const inInput = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if (inInput) return;

      if ((e.key === "g" || e.key === "G" || e.key === "/") && total > 1) {
        e.preventDefault();
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [total]);

  useEffect(() => {
    if (!isOpen) return;
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [isOpen]);

  const canPrev = activeIdx > 0;
  const canNext = activeIdx < total - 1;

  // metadata labels active only if at least one tab matches
  const hasMetadataLabels = useMemo(() => {
    if (courseKey === "flyt-og-rytme") return false;
    if (!tabLabelMap || !Array.isArray(tabsData) || tabsData.length === 0) return false;
    for (let i = 0; i < tabsData.length; i++) {
      const b = basenameOfTab(tabsData[i]);
      if (mapGet(tabLabelMap, b)) return true;
    }
    return false;
  }, [courseKey, tabLabelMap, tabsData]);

  const unitWord = courseKey === "flyt-og-rytme" ? "Exercise" : hasMetadataLabels ? null : "Part";

  const title = useMemo(() => {
    const exLabel = labelFor(activeIdx);
    const left = unitWord ? `${unitWord} ${exLabel}` : exLabel;
    return `${left} • ${activeIdx + 1}/${total}`;
  }, [activeIdx, total, labelFor, unitWord]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return null;

    const hits = [];
    for (let i = 0; i < total; i++) {
      const label = labelFor(i);
      const hay = (unitWord ? `${unitWord} ${label}` : `${label}`).toLowerCase();
      if (hay.includes(query)) hits.push(i);
    }
    return hits.slice(0, 30);
  }, [q, total, labelFor, unitWord]);

  const flytGroups = useMemo(() => buildFlytGroups(tabsData, courseKey), [tabsData, courseKey]);

  if (!total || total <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-2 relative">
      {/* Premium “glassy” container */}
      <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-xl p-1.5 border border-white/15 shadow-[0_6px_20px_rgba(0,0,0,0.10)]">
        <NavBtn title="First" disabled={!canPrev} onClick={() => handleTabClick(0)}>
          <IconFirst />
        </NavBtn>

        <NavBtn title="Previous" disabled={!canPrev} onClick={() => handleTabClick(activeIdx - 1)}>
          <IconPrev />
        </NavBtn>

        {/* Center readout */}
        <div className="px-2 sm:px-3 py-1 rounded-lg bg-white/15 border border-white/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.20)]">
          <div className="flex items-baseline gap-2">
            {unitWord && <span className="text-xs font-semibold text-white/70">{unitWord}</span>}
            <span className="text-sm font-semibold text-white tracking-tight">{labelFor(activeIdx)}</span>
            <span className="text-white/40">•</span>
            <span className="text-sm font-semibold text-white/95">
              {activeIdx + 1}/{total}
            </span>
          </div>
        </div>

        <NavBtn title="Next" disabled={!canNext} onClick={() => handleTabClick(activeIdx + 1)}>
          <IconNext />
        </NavBtn>

        <NavBtn title="Last" disabled={!canNext} onClick={() => handleTabClick(total - 1)}>
          <IconLast />
        </NavBtn>

        {/* Jump */}
        <div className="relative" ref={dropdownRef}>
          <PillBtn title="Jump (G)" onClick={() => setIsOpen((v) => !v)}>
            <span className="hidden sm:inline">Jump</span>
            <IconChevronDown />
          </PillBtn>

          {isOpen && (
<div
  className="
    absolute right-0 top-full mt-2
    w-[min(520px,calc(100vw-16px))]
    max-h-[80vh]
    bg-white/95 backdrop-blur-md
    rounded-2xl shadow-2xl
    border border-black/10
    p-4
    z-[999]
    overflow-hidden
  "
>              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-gray-500">Quick jump</div>
                  <div className="font-semibold text-gray-900">{title}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="w-9 h-9 rounded-xl bg-white/70 border border-black/10 hover:bg-white flex items-center justify-center text-gray-700 hover:shadow-sm active:scale-[0.98]"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="mt-3">
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={courseKey === "flyt-og-rytme" ? "Type: 5A, 12B…" : "Type to search…"}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 shadow-[inset_0_1px_0_rgba(0,0,0,0.04)]"
                />
                <div className="mt-1 text-[11px] text-gray-500">
                  Tip: press <span className="font-mono">G</span> or <span className="font-mono">/</span> to open.
                </div>
              </div>

              {filtered && (
                <div className="mt-3 max-h-56 overflow-y-auto rounded-xl border border-gray-100 bg-white">
                  {filtered.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500">No matches.</div>
                  ) : (
                    filtered.map((idx) => {
                      const isActive = idx === activeIdx;
                      return (
                        <button
                          key={idx}
                          type="button"
                          className={[
                            "w-full text-left px-4 py-3 text-sm flex items-center justify-between",
                            isActive ? "bg-blue-50 text-blue-800 font-medium" : "hover:bg-gray-50 text-gray-800",
                          ].join(" ")}
                          onClick={() => {
                            handleTabClick(idx);
                            setIsOpen(false);
                            setQ("");
                          }}
                        >
                          <span>
                            {unitWord ? `${unitWord} ` : ""}
                            {labelFor(idx)}
                          </span>
                          {isActive && <IconCheck />}
                        </button>
                      );
                    })
                  )}
                </div>
              )}

              {!filtered && (
                <div className="mt-4 space-y-3">
                  {flytGroups ? (
                    <>
                      <div className="text-xs font-medium text-gray-500">All exercises</div>
                      <div className="max-h-64 overflow-y-auto rounded-2xl border border-gray-100 bg-white p-3">
                        <div className="grid gap-2">
                          {flytGroups.map((g) => {
                            const aIdx = g.A;
                            const bIdx = g.B;
                            const aActive = aIdx === activeIdx;
                            const bActive = bIdx === activeIdx;

                            return (
                              <div key={g.n} className="flex items-center justify-between gap-3">
                                <div className="text-sm font-semibold text-gray-700 w-10 text-right">{g.n}</div>
                                <div className="flex items-center gap-2">
                                  <GridBtn
                                    disabled={typeof aIdx !== "number"}
                                    active={aActive}
                                    onClick={() => {
                                      if (typeof aIdx !== "number") return;
                                      handleTabClick(aIdx);
                                      setIsOpen(false);
                                    }}
                                  >
                                    A
                                  </GridBtn>
                                  <GridBtn
                                    disabled={typeof bIdx !== "number"}
                                    active={bActive}
                                    onClick={() => {
                                      if (typeof bIdx !== "number") return;
                                      handleTabClick(bIdx);
                                      setIsOpen(false);
                                    }}
                                  >
                                    B
                                  </GridBtn>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-xs font-medium text-gray-500">
                        {hasMetadataLabels ? "All sections" : "All parts"}
                      </div>
                      <div className="max-h-64 overflow-y-auto rounded-2xl border border-gray-100 bg-white">
                        {tabsData.map((_, idx) => {
                          const isActive = idx === activeIdx;
                          return (
                            <button
                              key={idx}
                              type="button"
                              className={[
                                "w-full text-left px-4 py-3 text-sm flex items-center justify-between",
                                isActive ? "bg-blue-50 text-blue-800 font-medium" : "hover:bg-gray-50 text-gray-800",
                              ].join(" ")}
                              onClick={() => {
                                handleTabClick(idx);
                                setIsOpen(false);
                              }}
                            >
                              <span>
                                {unitWord ? `${unitWord} ` : ""}
                                {labelFor(idx)}
                              </span>
                              {isActive && <IconCheck />}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-between pt-1 text-xs text-gray-500">
                    <span>ESC to close</span>
                    <span className="hidden sm:inline">Type to search</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesktopSelector;